/**
=========================================================
* Elite 247 Dashboard - Recent Content Component
=========================================================
*/

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// API
import { adminApi } from "services/api";
import { lightDark, DARK } from "utils/lightDark";

const ITEM_BORDER = lightDark("1px solid rgba(0,0,0,0.1)", `1px solid ${DARK.border}`);

// Small icon-only buttons are 25px; give them a finger-sized target on phones and tablets.
const viewButtonSx = ({ breakpoints }) => ({
  [breakpoints.down("md")]: { width: 36, height: 36, minWidth: 36, minHeight: 36 },
});

const truncate = (text, max) => (text.length > max ? `${text.substring(0, max)}...` : text);

function RecentContent() {
  const [tabValue, setTabValue] = useState(0);
  const [blogPosts, setBlogPosts] = useState([]);
  const [bootcamps, setBootcamps] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentContent();
  }, []);

  const fetchRecentContent = async () => {
    try {
      const [postsData, bootcampsData, servicesData] = await Promise.all([
        adminApi.getBlogPosts(),
        adminApi.getBootcamps(),
        adminApi.getServices(),
      ]);

      // Get the 5 most recent items (handle both array and object responses)
      setBlogPosts(
        Array.isArray(postsData) ? postsData.slice(0, 5) : (postsData.posts || []).slice(0, 5)
      );
      setBootcamps(Array.isArray(bootcampsData) ? bootcampsData.slice(0, 5) : []);
      setServices(Array.isArray(servicesData) ? servicesData.slice(0, 5) : []);
    } catch (error) {
      console.error("Failed to fetch recent content:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const renderBlogPosts = () => (
    <MDBox>
      {loading ? (
        <MDTypography variant="body2" color="text" sx={{ textAlign: "center", py: 3 }}>
          Loading...
        </MDTypography>
      ) : blogPosts.length === 0 ? (
        <MDTypography variant="body2" color="text" sx={{ textAlign: "center", py: 3 }}>
          No blog posts yet
        </MDTypography>
      ) : (
        blogPosts.map((post) => (
          <MDBox
            key={post._id}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            p={{ xs: 1.5, sm: 2 }}
            gap={{ xs: 1, md: 0 }}
            sx={{
              borderRadius: 2,
              border: ITEM_BORDER,
              transition: "all 0.3s",
              "&:hover": {
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <MDBox flex={1}>
              <MDTypography variant="button" fontWeight="medium" sx={{ display: "block", mb: 0.5 }}>
                {post.title}
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Chip
                  label={post.published ? "Published" : "Draft"}
                  size="small"
                  color={post.published ? "success" : "default"}
                  sx={{ height: 20, fontSize: "0.65rem" }}
                />
                <MDTypography variant="caption" color="text">
                  {formatDate(post.createdAt)}
                </MDTypography>
              </MDBox>
            </MDBox>
            <Tooltip title="View Post">
              <MDButton
                component={Link}
                to="/blog-posts"
                variant="text"
                color="info"
                iconOnly
                sx={viewButtonSx}
                size="small"
              >
                <Icon>visibility</Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ))
      )}
    </MDBox>
  );

  const renderBootcamps = () => (
    <MDBox>
      {loading ? (
        <MDTypography variant="body2" color="text" sx={{ textAlign: "center", py: 3 }}>
          Loading...
        </MDTypography>
      ) : bootcamps.length === 0 ? (
        <MDTypography variant="body2" color="text" sx={{ textAlign: "center", py: 3 }}>
          No bootcamps yet
        </MDTypography>
      ) : (
        bootcamps.map((bootcamp) => (
          <MDBox
            key={bootcamp._id}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            p={{ xs: 1.5, sm: 2 }}
            gap={{ xs: 1, md: 0 }}
            sx={{
              borderRadius: 2,
              border: ITEM_BORDER,
              transition: "all 0.3s",
              "&:hover": {
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <MDBox flex={1}>
              <MDTypography variant="button" fontWeight="medium" sx={{ display: "block", mb: 0.5 }}>
                {bootcamp.title}
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Chip
                  label={bootcamp.active ? "Active" : "Inactive"}
                  size="small"
                  color={bootcamp.active ? "success" : "default"}
                  sx={{ height: 20, fontSize: "0.65rem" }}
                />
                {bootcamp.price && (
                  <MDTypography variant="caption" color="text">
                    ${bootcamp.price}
                  </MDTypography>
                )}
              </MDBox>
            </MDBox>
            <Tooltip title="View Bootcamp">
              <MDButton
                component={Link}
                to="/bootcamps"
                variant="text"
                color="info"
                iconOnly
                sx={viewButtonSx}
                size="small"
              >
                <Icon>visibility</Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ))
      )}
    </MDBox>
  );

  const renderServices = () => (
    <MDBox>
      {loading ? (
        <MDTypography variant="body2" color="text" sx={{ textAlign: "center", py: 3 }}>
          Loading...
        </MDTypography>
      ) : services.length === 0 ? (
        <MDTypography variant="body2" color="text" sx={{ textAlign: "center", py: 3 }}>
          No services yet
        </MDTypography>
      ) : (
        services.map((service) => (
          <MDBox
            key={service._id}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            p={{ xs: 1.5, sm: 2 }}
            gap={{ xs: 1, md: 0 }}
            sx={{
              borderRadius: 2,
              border: ITEM_BORDER,
              transition: "all 0.3s",
              "&:hover": {
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <MDBox flex={1}>
              <MDTypography variant="button" fontWeight="medium" sx={{ display: "block", mb: 0.5 }}>
                {service.title}
              </MDTypography>
              {(service.shortDescription || service.description) && (
                <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                  {truncate(service.shortDescription || service.description, 60)}
                </MDTypography>
              )}
            </MDBox>
            <Tooltip title="View Service">
              <MDButton
                component={Link}
                to="/services"
                variant="text"
                color="info"
                iconOnly
                sx={viewButtonSx}
                size="small"
              >
                <Icon>visibility</Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ))
      )}
    </MDBox>
  );

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={{ xs: 2, sm: 3 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h6" fontWeight="medium">
            Recent Content
          </MDTypography>
          <Icon sx={{ color: ({ palette: { info } }) => info.main }}>content_paste</Icon>
        </MDBox>

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Blog Posts" />
          <Tab label="Bootcamps" />
          <Tab label="Services" />
        </Tabs>

        {tabValue === 0 && renderBlogPosts()}
        {tabValue === 1 && renderBootcamps()}
        {tabValue === 2 && renderServices()}
      </MDBox>
    </Card>
  );
}

export default RecentContent;
